import { createFileRoute, Outlet, redirect, useNavigate, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { NotebookPen, Settings } from "lucide-react";
import { AppShell, POINTER_QUERY } from "@/components/patterns/app-shell";
import { TabBar } from "@/components/patterns/tab-bar";
import { RailNav } from "@/app/components/RailNav";
import { useMediaQuery } from "@/hooks/use-media-query";
import { Logo } from "@/components/ui/logo";
import { Skeleton } from "@/components/ui/skeleton";
import { env } from "@/app/config/env";

/**
 * THE SIGNED-IN HALF OF THE APP.
 *
 * The guard turns on "anonymous" and NEVER on "unknown" — on launch the session token is
 * still being read out of the Keychain, and treating not-yet-known as signed-out bounces
 * every returning user to the sign-in screen for a frame. (The router is not mounted until
 * the session is known, so this branch is a second wall rather than the first.)
 *
 * The frame is the design system's shell: it owns the single scrolling region, the floating
 * navigation above the home indicator, and the safe-area insets. Screens pass content into
 * its slots and never position themselves.
 */
export const Route = createFileRoute("/_app")({
  beforeLoad: ({ context }) => {
    if (context.auth.status === "anonymous") throw redirect({ to: "/sign-in" });
  },
  component: AppLayout,
});

function AppLayout() {
  const { t } = useTranslation();
  const { auth } = Route.useRouteContext();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  /**
   * The shell turns the floating bar into a left rail above this width, but the bar itself
   * is shaped for a thumb and does not transform. Asked here, because the nav is built
   * before the shell mounts and so cannot read the shell's own layout context.
   */
  const isPointer = useMediaQuery(POINTER_QUERY);

  if (auth.status === "unknown") {
    return (
      <AppShell>
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-32 w-full" />
      </AppShell>
    );
  }

  const current = pathname.startsWith("/settings") ? "settings" : "notes";

  const navItems = [
    { id: "notes", label: t("nav.notes"), icon: <NotebookPen /> },
    { id: "settings", label: t("nav.settings"), icon: <Settings /> },
  ];

  function go(id: string) {
    void navigate({ to: id === "settings" ? "/settings" : "/" });
  }

  return (
    <AppShell
      /**
       * THE BRAND SLOT IS ALWAYS FILLED, and that is a rule rather than a nicety: the
       * system's logo falls back to the collection's own working name for it, and that name
       * must never appear in a product built on it. Passing this app's name is what keeps
       * someone else's codename off the screen.
       *
       * Phone screens show no brand mark at all (the shell only renders this on the rail),
       * so this is the pointer layout's mark.
       */
      brand={<Logo label={env.appName} interactive />}
      nav={
        isPointer ? (
          <RailNav value={current} onValueChange={go} items={navItems} />
        ) : (
          <TabBar value={current} onValueChange={go} items={navItems} />
        )
      }
    >
      <Outlet />
    </AppShell>
  );
}
